import React from 'react';
import Script from 'next/script';
import dynamic from 'next/dynamic';

import { ParsedUrlQuery } from 'node:querystring';
import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next';

import { fetchProduct } from '@/fetchers/products';
import { IProduct } from '@/models/product';

import {
  getLangCountry,
  getRealLangCountry,
  SUPPORTED_LANG
} from '@/helpers/languages';

import { fetchAlternateUrls } from '@/fetchers/commons';
import { AlternateLangs } from '@/models/app';

import { SUPPORTED_COUNTRY } from '@/helpers/countries';

import MyHead from '@/components/MyHead';

import Overview from '@/components/v2/Pages/Product/Overview';
import Technologies from '@/components/v2/Pages/Product/Technologies';
import Related from '@/components/v2/Pages/Product/Related';
import Materials from '@/components/v2/Pages/Product/Materials';
import ShoeCharacteristics from '@/components/v2/Pages/Product/ShoeCharacteristics';

import { useProduct } from '@/hooks/products/use-product';

const FeedatyVerticalReviews = dynamic(
  () => import('@/components/v2/Pages/Product/Feedaty/FeedatyVerticalReviews'),
  { ssr: false }
);

export default function ProductPage({
  product: serverProduct,
  alternates
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const { data: product } = useProduct(serverProduct.slug, {
    initialData: serverProduct,
    refetchOnMount: 'always'
  });

  if (!product) {
    return null;
  }
  const meta_title =
    product.meta_title && product.meta_title.length > 0
      ? product.meta_title
      : product.productName;
  const meta_description =
    product.meta_description && product.meta_description.length > 0
      ? product.meta_description
      : product.short_description && product.short_description.length > 0
      ? product.short_description
      : product.description;
  return (
    <>
      <MyHead
        title={meta_title}
        description={meta_description}
        alternates={alternates}
      />

      <Overview product={product} />
      {/* <PromoVideo product={product} /> */}
      <Technologies product={product} />
      <Materials product={product} />
      <FeedatyVerticalReviews product={product} />
      <ShoeCharacteristics product={product} />
      <Related product={product} />

      <Script
        strategy="beforeInteractive"
        className="feedaty_sdk"
        src="https://widget.feedaty.com/v3.0.0/js/2021/10215377/feedaty.min.js"
      />

      <Script key={product.selling_code} strategy="lazyOnload">
        {`(function () {
            FeedatyWidget.firstLoad = true
            FeedatyWidget.init()
            FeedatyWidget.firstLoad = false
            

            var t = new Event("FeedatyLoaded", {
                bubbles: !0,
                cancelable: !1
            });

            document.dispatchEvent(t)
          })();`}
      </Script>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

interface Params extends ParsedUrlQuery {
  slug: string;
}

export const getStaticProps: GetStaticProps<{
  product: IProduct;
  alternates: AlternateLangs[];
}> = async ctx => {
  const { slug } = ctx.params as Params;

  const real = getRealLangCountry(ctx.params);

  if (
    !SUPPORTED_LANG.includes(real.lang) ||
    !SUPPORTED_COUNTRY.includes(real.country)
  ) {
    return { notFound: true };
  }

  const [product, alternates] = await Promise.all([
    fetchProduct({
      ...getLangCountry(ctx.params),
      slug
    }),
    fetchAlternateUrls({
      url: slug,
      ...getLangCountry(ctx.params),
      prefix: ''
    })
  ]);

  if (!product) {
    return {
      notFound: true,
      revalidate: 60
    };
  }

  return {
    props: {
      product,
      alternates
    },
    revalidate: 86_400
  };
};
